import React, { useCallback, useMemo } from 'react';
import { InfoIcon, Paper, Typography } from '@cherre-frontend/ui';
import { useAppContext } from '@cherre-frontend/core';
import { useCherreValue } from '@cherre-frontend/data-fetching';
import { castDSPDevMode, useDspWorkflowMode } from './useDspWorkflowMode';
import { WorkflowModes, WorkflowModesID } from '../../__generated__/constants';
import {
  DecodedToken,
  DSPHasuraJWT,
} from 'src/products/shell/DSPRelayEnvironment/jwt';
import { useHistory, useLocation } from 'react-router';
import TopBarSelect from 'src/components/TopBarSelect';

export const parseTokenWorkflowModes = (
  hasuraToken: DecodedToken | null | undefined
): WorkflowModesID[] => {
  if (!hasuraToken) {
    return [];
  }

  const workflowModes = hasuraToken['https://hasura.io/jwt/claims'][
    'x-hasura-allowed-workflow-mode'
  ]
    ?.replace('{', '')
    .replace('}', '')
    .split(',')
    .map((mode) => Number(mode.trim()));

  return workflowModes as WorkflowModesID[];
};

const tokenContainsWorkflowMode = (
  hasuraToken: DecodedToken | null | undefined,
  mode: WorkflowModesID
) => {
  return hasuraToken && parseTokenWorkflowModes(hasuraToken).includes(mode);
};

const submissionPaths = [
  '/dsp/preparer/submissions',
  '/dsp/reviewer/submissions',
  '/dsp/view_only/submissions',
  '/dsp/view_admin/submissions',
];

const validPaths = [
  '/dsp/submissions-insights',
  '/dsp/batch-management',
  ...submissionPaths,
];

const isTestModeValidLocation = (pathname: string) => {
  for (const path of validPaths) {
    if (pathname.startsWith(path)) {
      return true;
    }
  }

  return false;
};

const ToastMessage = (message) => (
  <Paper
    sx={{
      borderRadius: '10px',
      backgroundColor: (theme) => theme.palette.primary.light,
      color: (theme) => theme.palette.common.white,
      display: 'flex',
      width: '860px',
      alignItems: 'center',
      padding: '10px',
      gap: '10px',
      fontWeight: '700',
    }}
  >
    <InfoIcon />
    <Typography component={'span'}>{message}</Typography>
  </Paper>
);

export const DSPHeaderActions: React.FC = () => {
  const [mode, setMode] = useDspWorkflowMode();
  const context = useAppContext();
  const location = useLocation();
  const { push } = useHistory();

  const hasuraToken = useCherreValue(DSPHasuraJWT.getInstance().atom);

  const onChange = useCallback((value) => {
    const mode = castDSPDevMode(value);
    setMode(mode);

    const submissionPath = submissionPaths.find((path) =>
      location.pathname.startsWith(path)
    );

    if (submissionPath) {
      // Redirect to the submission list page
      push(submissionPath);
    }

    if (mode !== WorkflowModes.TEST.id) {
      return;
    }

    context.showSnackbar({
      message:
        'You’re now in submission test mode. Data will not be egressed to production while this is turned on.',
      type: 'info',
      content: (key, message) => ToastMessage(message),
    });
  }, []);

  if (!tokenContainsWorkflowMode(hasuraToken, WorkflowModes.TEST.id)) {
    return null;
  }

  if (!isTestModeValidLocation(location.pathname)) {
    return null;
  }

  const options = useMemo(() => {
    return [
      {
        id: WorkflowModes.PRODUCTION.id,
        value: WorkflowModes.PRODUCTION.id,
        disabled: !tokenContainsWorkflowMode(
          hasuraToken,
          WorkflowModes.PRODUCTION.id
        ),
        label: 'Production',
      },
      {
        id: WorkflowModes.TEST.id,
        value: WorkflowModes.TEST.id,
        disabled: !tokenContainsWorkflowMode(
          hasuraToken,
          WorkflowModes.TEST.id
        ),
        label: 'Submission Test',
      },
    ];
  }, [hasuraToken]);

  const tooltipText = useMemo(
    () =>
      mode === WorkflowModes.TEST.id
        ? 'Test submissions without affecting live data.'
        : 'Live mode with all features fully operational and affecting live data.',
    [mode]
  );

  return (
    <TopBarSelect
      value={mode}
      options={options}
      onChange={onChange}
      tooltipText={tooltipText}
    />
  );
};

export default DSPHeaderActions;
